import { useMemo, useState, useEffect, useCallback } from "react";
import { IViolationModuleLean, ICrewScheduleChecks } from "@vmosolutions/oc-interfaces";
import { SelectField } from "../../../MUI5FormikComponents/SelectField";
import { Grid, Button, Typography, Modal, Paper, ButtonGroup, Box, Tooltip } from "@mui/material";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import _ from "lodash";
import { getAvailableViolationModules } from "src/redux/actions/CrewSchedule/violations";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { DateTimeField } from "src/components/MUI5FormikComponents/DateTimeField";
import moment from "moment-timezone";
import { CrewFormType } from "./CrewForm";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

interface ICrewScheduleChecksProps {
  crewForm: CrewFormType;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  showScheduleChecksModal: boolean;
  setShowScheduleChecksModal: any;
}

interface ICheckErrors {
  regId: string;
  start: string;
  backDataDate: string;
}

const defaultErrors: ICheckErrors = {
  regId: "",
  start: "",
  backDataDate: "",
};

export const CrewScheduleChecksField = (props: ICrewScheduleChecksProps): JSX.Element => {
  const { crewForm, showScheduleChecksModal, setActiveModal, setShowScheduleChecksModal } = props; // setActiveModal
  const { cancellablePromise } = useCancellablePromise();

  const [availableViolationModules, setAvailableViolationModules] = useState<IViolationModuleLean[]>([]);
  useEffect(() => {
    const fetchViolationModules = async (): Promise<void> => {
      const violModules = await cancellablePromise(getAvailableViolationModules());
      if (violModules && Array.isArray(violModules)) {
        setAvailableViolationModules(violModules);
      }
    };
    if (!availableViolationModules?.length) void fetchViolationModules();
  }, [availableViolationModules]);

  const scheduleChecks = useMemo(
    () => crewForm?.values?.crewInfo?.scheduleChecks ?? [],
    [crewForm?.values?.crewInfo?.scheduleChecks]
  );

  const [selectedCheck, setSelectedCheck] = useState<ICrewScheduleChecks | null>(null);
  const [updatedCheck, setUpdatedCheck] = useState<ICrewScheduleChecks | null>(null);
  const isValid = !!updatedCheck?.regId && updatedCheck?.displayName && updatedCheck?.start;
  const dirty = useMemo(() => !_.isEqual(selectedCheck, updatedCheck), [selectedCheck, updatedCheck]);
  const duplicate = useMemo(
    () => !!scheduleChecks?.find((check) => check?.regId === updatedCheck?.regId) && !selectedCheck,
    [scheduleChecks, updatedCheck, selectedCheck]
  );

  const crewId = useMemo(() => crewForm?.values?._id, [crewForm?.values?._id]);
  useEffect(() => {
    setSelectedCheck(null);
    setUpdatedCheck(null);
  }, [crewId]);

  const [errors, setErrors] = useState<ICheckErrors>(defaultErrors);
  const checkFields = useCallback((v: ICrewScheduleChecks) => {
    setErrors({
      regId: v?.regId ? "" : "Regulation required",
      start: !!v?.end && v?.end < v?.start ? "Start has to be before end date" : v?.start ? "" : "Check Start required",
      backDataDate: !!v?.backDataDate && v?.backDataDate > v?.start ? "Back Data Date cannot be before start" : "",
    });
  }, []);

  const handleSelectCheck = useCallback((selectedCheck: ICrewScheduleChecks | null): void => {
    setSelectedCheck(selectedCheck);
    setUpdatedCheck(selectedCheck);
  }, []);

  const updateCheckVals = (updatedValue: ICrewScheduleChecks[]) => {
    crewForm?.setFieldValue("crewInfo.scheduleChecks", updatedValue);
    crewForm?.setFieldTouched("crewInfo.scheduleChecks", true);
  };

  const handleUpdateChecks = useCallback(
    (check?: ICrewScheduleChecks, deleteCheck?: boolean) => {
      const updatedValue = crewForm?.values?.crewInfo?.scheduleChecks?.length
        ? [...crewForm.values.crewInfo.scheduleChecks]
        : [];
      if (check) {
        const findIndex = updatedValue?.findIndex((c) => c?.regId === check?.regId);
        if (findIndex !== -1 && !deleteCheck) {
          setSelectedCheck(updatedCheck);
          updatedValue[findIndex] = updatedCheck;
          updateCheckVals(updatedValue);
        } else if (findIndex !== -1 && deleteCheck) {
          setActiveModal({
            title: "Delete Schedule Check",
            message: "Please confirm that you want to delete this Schedule Check",
            cancel: () => setActiveModal(null),
            confirm: (): Promise<void> => {
              setSelectedCheck(null);
              setUpdatedCheck(null);
              updatedValue.splice(findIndex, 1);
              updateCheckVals(updatedValue);
              setActiveModal(null);
              return null;
            },
            confirmLabel: "Confirm",
          });
        }
      } else {
        updatedValue.push(updatedCheck);
        updateCheckVals(updatedValue);
      }
    },
    [crewForm, updatedCheck, setActiveModal]
  );

  return (
    <Modal open={showScheduleChecksModal}>
      <Paper elevation={4} sx={{ width: "65%", margin: "auto", marginTop: "10%", p: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={8} sx={{ maxHeight: 400, overflowY: "scroll" }}>
            {scheduleChecks.map((check: ICrewScheduleChecks, key: number) => {
              return (
                <DraggableListItem
                  active={selectedCheck?.regId === check.regId && selectedCheck?.start === check.start}
                  key={key}
                  sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", cursor: "pointer" }}
                  onClick={() => handleSelectCheck(check)}
                >
                  <Box>{check.displayName}</Box>
                  <Box>
                    {moment(check.start).tz("UTC").format("MMM D, YYYY")}
                    {check?.end && (
                      <>
                        {" - "}
                        {moment(check.end).tz("UTC").format("MMM DD, YYYY")}
                      </>
                    )}
                  </Box>
                  {check?.backDataDate && (
                    <Tooltip title="Backdate Data Date">
                      <Box>{moment(check.backDataDate).tz("UTC").format("MMM DD, YYYY")}</Box>
                    </Tooltip>
                  )}
                  <Box
                    sx={{ cursor: "pointer", alignItems: "center", display: "flex", justifyContent: "space-between" }}
                  >
                    <EditIcon fontSize="small" sx={{ mr: 1 }} onClick={() => handleSelectCheck(check)} />
                    <DeleteIcon
                      fontSize="small"
                      onClick={() => {
                        handleUpdateChecks(check, true);
                      }}
                    />
                  </Box>
                </DraggableListItem>
              );
            })}
            <Button color="success" variant="contained" disableRipple fullWidth onClick={() => handleSelectCheck(null)}>
              <AddIcon fontSize="small" />
              Add Schedule Check
            </Button>
          </Grid>
          <Grid container item xs={4}>
            <Grid item xs={12}>
              <Typography variant="h6">Crew Schedule Checks</Typography>
              <SelectField
                label="Regulation"
                name="violationModules"
                maxMenuHeight={250}
                getOptionLabel={(violationModule: IViolationModuleLean) => violationModule?.displayName ?? ""}
                getOptionValue={(violationModule: IViolationModuleLean) => violationModule?.regId ?? ""}
                options={availableViolationModules}
                value={availableViolationModules?.find((m) => m?.regId === updatedCheck?.regId)}
                onChange={(violationModule: IViolationModuleLean) => {
                  const { regId, displayName } = violationModule;
                  setUpdatedCheck((prev) => ({ ...prev, regId, displayName }));
                  checkFields({ ...updatedCheck, regId, displayName });
                }}
                closeMenuOnSelect={true}
                isMulti={false}
                placeholder={availableViolationModules?.length === 0 ? "None Available..." : "Select Regulation..."}
                disabled={availableViolationModules?.length === 0}
                meta={{ error: errors.regId }}
              />
              <DateTimeField
                label="Check Start"
                name="start"
                value={updatedCheck?.start && updatedCheck.start.valueOf()}
                onChange={(start: any) => {
                  setUpdatedCheck((prev) => ({ ...prev, start }));
                  checkFields({ ...updatedCheck, start });
                }}
                hideTimeControl={true}
                meta={{ error: errors.start }}
                disabled={availableViolationModules?.length === 0}
              />
              <DateTimeField
                label="Check End"
                name="end"
                value={updatedCheck?.end && updatedCheck.end.valueOf()}
                onChange={(end: any) => {
                  setUpdatedCheck((prev) => ({ ...prev, end }));
                  checkFields({ ...updatedCheck, end });
                }}
                hideTimeControl={true}
                disabled={availableViolationModules?.length === 0}
              />
              <DateTimeField
                label="Back Data Date"
                name="backDataDate"
                value={updatedCheck?.backDataDate && updatedCheck.backDataDate.valueOf()}
                onChange={(backDataDate: any) => {
                  setUpdatedCheck((prev) => ({ ...prev, backDataDate }));
                  checkFields({ ...updatedCheck, backDataDate });
                }}
                hideTimeControl={true}
                meta={{ error: errors.backDataDate }}
                disabled={availableViolationModules?.length === 0}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "end", p: 2 }}>
              <ButtonGroup variant="contained" size="small">
                <Button
                  type="reset"
                  color="warning"
                  disabled={!dirty}
                  onClick={() => {
                    setUpdatedCheck(selectedCheck);
                    setErrors(defaultErrors);
                  }}
                >
                  Reset
                </Button>
                <Button
                  color="success"
                  disabled={
                    !isValid ||
                    !dirty ||
                    duplicate ||
                    errors?.start?.length > 0 ||
                    errors?.regId?.length > 0 ||
                    errors?.backDataDate?.length > 0
                  }
                  onClick={() => {
                    if (selectedCheck) {
                      handleUpdateChecks(selectedCheck);
                    } else {
                      handleUpdateChecks();
                    }
                  }}
                  type="submit"
                >
                  Save
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", pt: 2, borderTop: "1px solid grey" }} justifyContent="end">
            <Button
              size="small"
              onClick={() => {
                setShowScheduleChecksModal(false);
                setSelectedCheck(null);
                setUpdatedCheck(null);
              }}
              variant="contained"
              color="error"
            >
              Close
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
};
